import { useState, useEffect, useCallback } from 'react';
import { enhancedApi } from '../../../shared/services/enhancedApi';

export interface DestinationProvinces {
  destinationId: number;
  destinationName: string;
  destinationCode: string;
  areaType: string;
}

interface UseDestinationProvincesReturn {
  destinations: DestinationProvinces[];
  startAndEndlocationOptions: string[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useProvinces = (): UseDestinationProvincesReturn => {
  const [destinations, setDestinationProvinces] = useState<DestinationProvinces[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDestinationProvinces = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Use enhanced API with caching and queuing
      const response = await enhancedApi.cachedGet<{
        success: boolean;
        destinations: DestinationProvinces[];
      }>(
        '/api/destinations/by-area?area=province',
        {},
        30 * 60 * 1000 // 30 minutes TTL - provinces rarely change
      );

      if (response.success && response.destinations) {
        const sortedProvinces = [...response.destinations].sort((a, b) =>
          a.destinationName.localeCompare(b.destinationName)
        );

        console.log("Fetched provinces (cached/sorted):", sortedProvinces);

        setDestinationProvinces(sortedProvinces);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Error fetching provinces:', err);
      setError('Failed to fetch provinces');
    } finally {
      setLoading(false);
    }
  }, []); // No dependencies needed since api and setters are stable

  useEffect(() => {
    fetchDestinationProvinces();
  }, [fetchDestinationProvinces]); // ✅ Now includes the dependency

  // Convert provinces to location options format for dropdowns
  const startAndEndlocationOptions = destinations.map(destination => destination.destinationName);

  return {
    destinations,
    startAndEndlocationOptions,
    loading,
    error,
    refetch: fetchDestinationProvinces
  };
};